import React, { useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import NotificationBell from './NotificationBell';
import CollectionSelector, { CollectionType } from './inventory/CollectionSelector';
import { ModeType } from '../contexts/ModeContext';
import { useSpace } from '../contexts/SpaceContext';
import '../styles/components/unifiedHeader.css';

interface UnifiedHeaderProps {
  // Header props
  title?: string;
  subtitle?: React.ReactNode;
  showFontSizeToggle?: boolean;
  onFontSizeToggle?: () => void;
  fontSize?: string; // Current font size for display
  
  // Collection dropdown props (for Inventory page)
  showCollectionDropdown?: boolean;
  selectedCollection?: CollectionType;
  onCollectionChange?: (collection: CollectionType) => void;
  onShowCollectionDetail?: (collectionId: number | undefined, collectionName: string, itemCount: number) => void;
  onDeleteCollection?: (collectionId: number, name: string) => void;
  onShareCollection?: (collectionId: number) => void;
  onEmptyCollection?: (collectionId: number, name: string) => void;
  collectionRefreshTrigger?: number;
  allItemsCount?: number; // Total count of all items
  wanderingItemsCount?: number; // Count of items not in any collection
  // Callback to get a function that toggles the collection sheet
  onRegisterToggleSheet?: (toggleFn: () => void) => void;
  hideKitchenCollections?: boolean;
  
  // Notification props
  showNotifications?: boolean;
  onNavigateToCollection?: (collectionId: number) => void;
  onNavigateToGroup?: (groupId: number) => void;
  
  // Data space selector props
  activeSpaceName?: string;
  activeMode?: ModeType;
  onOpenAppContext?: () => void;
  
  // Sock icon props
  onSockClick?: () => void;
  isVoiceListening?: boolean;
  isTumbling?: boolean; // Trigger forward tumble animation on checkbox click
  isTumblingBackward?: boolean; // Trigger backward tumble animation on undo
  
  // Menu bar content
  menuBar?: React.ReactNode;
  
  // Optional: hide the default header and only show menu bar
  hideHeader?: boolean;

}

/**
 * UnifiedHeader - Combines app header and menu bar into a single fixed component
 * This ensures they're always positioned correctly relative to each other
 * Works in both web app and Capacitor without CSS positioning issues
 * 
 * In Capacitor, uses a portal to render at body level to ensure proper fixed positioning
 */
export const UnifiedHeader: React.FC<UnifiedHeaderProps> = ({
  title = 'Wandering Sock',
  subtitle = 'Photos → Searchable Items',
  showFontSizeToggle = true,
  onFontSizeToggle,
  fontSize = '2',
  showCollectionDropdown = false,
  selectedCollection,
  onCollectionChange,
  onShowCollectionDetail,
  onDeleteCollection,
  onShareCollection,
  onEmptyCollection,
  collectionRefreshTrigger,
  allItemsCount,
  wanderingItemsCount,
  onRegisterToggleSheet,
  hideKitchenCollections,
  activeSpaceName,
  activeMode,
  onOpenAppContext,
  showNotifications = false,
  onNavigateToCollection,
  onNavigateToGroup,
  onSockClick,
  isVoiceListening = false,
  isTumbling = false,
  isTumblingBackward = false,
  menuBar,
  hideHeader = false,
}) => {
  const isCapacitor = document.documentElement.classList.contains('capacitor-app');
  const { spaces } = useSpace();
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [isCollectionDropdownOpen, setIsCollectionDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isOpenRef = useRef(isCollectionDropdownOpen);
  
  // Keep ref in sync with state
  React.useEffect(() => {
    isOpenRef.current = isCollectionDropdownOpen;
  }, [isCollectionDropdownOpen]);
  
  // Register the toggle function with parent so it can be called directly
  React.useEffect(() => {
    if (onRegisterToggleSheet) {
      onRegisterToggleSheet(() => {
        setIsCollectionDropdownOpen(!isOpenRef.current);
      });
    }
  }, [onRegisterToggleSheet]);

  const handleUnreadCountChange = useCallback((count: number) => {
    setHasUnreadNotifications(count > 0);
  }, []);

  // Close dropdown when clicking outside (but not when clicking inside bottom sheet portal or tab bar)
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is inside the dropdown ref
      if (dropdownRef.current && dropdownRef.current.contains(target)) {
        return;
      }
      // Check if click is inside the bottom sheet (rendered via portal)
      const bottomSheet = document.querySelector('.collection-bottom-sheet-overlay');
      if (bottomSheet && bottomSheet.contains(target)) {
        return;
      }
      // Check if click is inside the tab bar - let tab handler manage this
      const tabBar = document.querySelector('.bottom-navigation');
      if (tabBar && tabBar.contains(target)) {
        return;
      }
      setIsCollectionDropdownOpen(false);
    };

    if (isCollectionDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCollectionDropdownOpen]);

  const handleCollectionSelect = (collection: CollectionType) => {
    onCollectionChange?.(collection);
    setIsCollectionDropdownOpen(false);
  };

  const headerRef = useRef<HTMLDivElement | null>(null);
  
  // Initialize portal container immediately, not in effect
  const portalRoot = useRef<HTMLElement | null>(null);
  if (isCapacitor && !portalRoot.current) {
    let container = document.getElementById('unified-header-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'unified-header-portal';
      document.body.appendChild(container);
    }
    portalRoot.current = container;
  }

  const headerContent = (
    <div className="unified-header" ref={headerRef}>
      {/* App Header */}
      {!hideHeader && (
        <div className="unified-header-top">
          <img 
            src="/happy_sock.png" 
            alt="Wandering Sock" 
            className={`unified-header-icon ${isVoiceListening ? 'listening' : ''} ${onSockClick ? 'clickable' : ''} ${isTumbling ? 'tumbling' : ''} ${isTumblingBackward ? 'tumbling-backward' : ''}`}
            onClick={onSockClick}
            role={onSockClick ? 'button' : undefined}
            aria-label={onSockClick ? 'Voice search' : undefined}
            tabIndex={onSockClick ? 0 : undefined}
          />
          
          <div className="unified-header-brand">
            {activeSpaceName && spaces.length > 1 && (
              <div className="unified-header-space-selector">
                <button
                  className="space-name-badge"
                  onClick={onOpenAppContext}
                  aria-label={`Switch space: ${activeSpaceName}`}
                >
                  {activeSpaceName}
                </button>
              </div>
            )}
            
            <div className="unified-header-main-title">
              {showCollectionDropdown && selectedCollection !== undefined && onCollectionChange ? (
                <div className="unified-header-collection-dropdown" ref={dropdownRef}>
                  <button 
                    className={`unified-header-title clickable ${title.length > 12 ? 'long-name' : ''}`}
                    onClick={() => setIsCollectionDropdownOpen(!isCollectionDropdownOpen)}
                    aria-expanded={isCollectionDropdownOpen}
                    aria-label="Select collection"
                  >
                    {title}
                    <ChevronDown size={16} className={`unified-header-chevron ${isCollectionDropdownOpen ? 'open' : ''}`} />
                  </button>
                  <div className="unified-header-subtitle">{subtitle}</div>
                </div>
              ) : (
                <div className="unified-header-text">
                  <button
                    className={`unified-header-title clickable app-context-trigger ${title.length > 12 ? 'long-name' : ''}`}
                    onClick={onOpenAppContext}
                    aria-label="DATA SPACE"
                  >
                    {title}
                    <ChevronDown size={14} className="unified-header-chevron" />
                  </button>
                  <div className="unified-header-subtitle">{subtitle}</div>
                </div>
              )}
            </div>
            {/* Collection sheet - rendered always when collection props available so it works in all modes */}
            {selectedCollection !== undefined && onCollectionChange && (
              <CollectionSelector
                selected={selectedCollection}
                onChange={handleCollectionSelect}
                onShowCollectionDetail={onShowCollectionDetail}
                onDeleteCollection={onDeleteCollection}
                onShareCollection={onShareCollection}
                onEmptyCollection={onEmptyCollection}
                refreshTrigger={collectionRefreshTrigger}
                allItemsCount={allItemsCount}
                wanderingItemsCount={wanderingItemsCount}
                renderAsBottomSheet={true}
                isBottomSheetOpen={isCollectionDropdownOpen}
                onBottomSheetClose={() => setIsCollectionDropdownOpen(false)}
                hideKitchenCollections={hideKitchenCollections}
                onOpenAppContext={onOpenAppContext}
              />
            )}
          </div>
          
          <div className="unified-header-actions">
            {/* Always render NotificationBell to poll, but hide visually when no unread */}
            {showNotifications && (
              <NotificationBell
                onNavigateToCollection={onNavigateToCollection}
                onNavigateToGroup={onNavigateToGroup}
                onUnreadCountChange={handleUnreadCountChange}
                larger
                hidden={!hasUnreadNotifications}
              />
            )}
            {/* Show view settings toggle when no unread notifications */}
            {showFontSizeToggle && (!showNotifications || !hasUnreadNotifications) && (
              <button 
                className="unified-header-font-toggle"
                onClick={onFontSizeToggle}
                aria-label="View settings"
              >
                <span className="font-toggle-text">Aa</span>
                <span className="font-toggle-bars">
                  <span className={`bar bar-1 ${parseInt(fontSize) >= 1 ? 'active' : ''}`}></span>
                  <span className={`bar bar-2 ${parseInt(fontSize) >= 2 ? 'active' : ''}`}></span>
                  <span className={`bar bar-3 ${parseInt(fontSize) >= 3 ? 'active' : ''}`}></span>
                  <span className={`bar bar-4 ${parseInt(fontSize) >= 4 ? 'active' : ''}`}></span>
                </span>
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Menu Bar */}
      {menuBar && (
        <div className="unified-header-menu">
          {menuBar}
        </div>
      )}
    </div>
  );

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;

    const update = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) {
        document.documentElement.style.setProperty('--unified-header-offset', `${Math.ceil(h)}px`);
      }
    };

    update();

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }

    return () => {
      if (ro) {
        ro.disconnect();
      } else {
        window.removeEventListener('resize', update);
      }
    };
  }, []);

  // In Capacitor, render via portal at body level for proper fixed positioning
  if (isCapacitor && portalRoot.current) {
    return createPortal(headerContent, portalRoot.current);
  }

  // In web app, render normally
  return headerContent;
};
